import React, { useState, useEffect, useCallback, useRef } from "react";
import Board, { moveCard } from "@lourenci/react-kanban";
import "@lourenci/react-kanban/dist/styles.css";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SettingsIcon from "@mui/icons-material/Settings";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Grid,
  Select,
  MenuItem,
  Popover,
  DialogContentText,
  colors,
  Avatar,
  CircularProgress,
  Alert,
  OutlinedInput,
  InputLabel,
  FormHelperText,
  FormControl,
  Card,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  InputAdornment,
} from "@mui/material";
import AddAlertIcon from "@mui/icons-material/AddAlert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { InputTextarea } from "primereact/inputtextarea";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import HistoryIcon from "@mui/icons-material/History";
import EmailHistory from "../Emailing/EmailHistory";

import StarIcon from "@mui/icons-material/Star";
import { COLORS } from "colors/color";
import useSignalingStore from "../signalingStore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { styled } from "@mui/material/styles";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";

import mail_icon from "../../assets/img/mail_icon.svg";
import ai_icon from "../../assets/img/ai_icon.svg";
import recycle_icon from "../../assets/img/recycle_icon.svg";
import ring_icon from "../../assets/img/ring_icon.svg";
import { BlocksIcon, MessageCircleWarningIcon } from "lucide-react";
import { Block, Cancel } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { OutlinedButton } from "../../components/Buttons/Buttons";
import CustomAlert from "../../components/Dialog/CustomAlert";
import SimpleAlert from "components/Dialog/SimpleAlert";

const WhiteBoard = styled(Board)({
  backgroundColor: "white",
});
const CompanyLogo = styled(Box)(({ theme }) => ({
  minWidth: "60px",
  minHeight: "60px",
  maxWidth: "60px",
  maxHeight: "60px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  borderRadius: 29,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    minWidth: "80px",
    minHeight: "80px",
    maxWidth: "80px",
    maxHeight: "80px",
    borderRadius: 20,
  },
}));

const ActionMenuContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px", // Further reduced from 28px
  height: "22px",
  marginLeft: "auto",
  "& .action-buttons": {
    position: "absolute",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 30,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-40px",
    left: "-80px",
    right: "-10px",
    bottom: "-40px",
    display: "none",
    zIndex: 5,
  },
  "&:hover::before": {
    display: "block",
  },
  "&:hover .action-buttons": {
    pointerEvents: "auto",
  },
  "& .action-item": {
    position: "absolute",
    opacity: 0,
    visibility: "hidden",
    transition: "all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    transform: "scale(0.5) rotate(-180deg)",
    zIndex: 10,
  },
  "&:hover .action-item": {
    opacity: 1,
    visibility: "visible",
  },
  // Tornado / Circular Fan-out positions (Optimized for 4 items in a tight arc)
  "&:hover .item-1": { transform: "translate(-32px, -24px) scale(1) rotate(360deg)" },
  "&:hover .item-2": { transform: "translate(-48px, -4px) scale(1) rotate(360deg)" },
  "&:hover .item-3": { transform: "translate(-44px, 18px) scale(1) rotate(360deg)" },
  "&:hover .item-4": { transform: "translate(-22px, 34px) scale(1) rotate(360deg)" },
  
  "& .settings-trigger": {
    transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s ease",
    color: "#0017E7",
    backgroundColor: "rgba(0, 23, 231, 0.05)",
    borderRadius: "50%",
    padding: "3px", // Reduced from 4px
    zIndex: 20,
    cursor: "pointer",
  },
  "&:hover .settings-trigger": {
    transform: "rotate(180deg) scale(1.1)",
    backgroundColor: "rgba(0, 23, 231, 0.12)",
    color: "#0000aa",
  },
}));

const ActionButton = styled(IconButton)(({ theme, bgcolor, textcolor }) => ({
  width: "24px", // Further reduced from 28px
  height: "24px",
  backgroundColor: bgcolor || "#fff",
  color: textcolor || "#fff",
  boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
  border: "1px solid rgba(255,255,255,0.2)",
  "&:hover": {
    backgroundColor: bgcolor,
    filter: "brightness(1.1)",
    transform: "scale(1.15)",
    boxShadow: "0 6px 14px rgba(0,0,0,0.2)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "13px", // Further reduced from 16px
  },
  "& img": {
    width: "11px", // Further reduced from 14px
    height: "11px",
    filter: "brightness(0) invert(1)",
  },
}));
export function ControlledBoard({
  candidates,
  onUpdateStep,
  steps,
  setSteps,
  createStep,
  deleteSteps,
  post,
  onCardClick,
  selectedCandidateId,
}) {
  const [controlledBoard, setBoard] = useState({ columns: [] });
  const boardCardRefs = useRef({});
  const lastForcedId = useRef(null);

  const [eventTitle, setEventTitle] = useState("");
  const [eventDate, setEventDate] = useState(dayjs().subtract(1, "day"));
  const [checkboxChecked, setCheckboxChecked] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [userId, setUserId] = useState("");
  const [recruiterName, setRecruiterName] = useState("");
  const [recruiterEmail, setRecruiterEmail] = useState("");
  const [recruiterPhone, setRecruiterPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [interviewType, setInterviewType] = useState("");
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false); // Track loading state
  const [reason, setReason] = useState("");

  const [newTemplate, setNewTemplate] = useState({ name: "", content: "" });
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [addTemplateDialogOpen, setAddTemplateDialogOpen] = useState(false);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isEmailInterfaceVisible, setIsEmailInterfaceVisible] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [templateSearchValue, setTemplateSearchValue] = useState("");
  const [emailFormData, setEmailFormData] = useState({
    recipient_email: "",
    placeholder_values: {},
  });
  const [emailPlaceholders, setEmailPlaceholders] = useState([]);
  const [renderedEmail, setRenderedEmail] = useState({
    subject: "",
    body: "",
  });
  const [emailErrors, setEmailErrors] = useState({});
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ type: null, message: "" });
  const [isEmailHistoryOpen, setIsEmailHistoryOpen] = useState(false);
  const [isVisualTemplate, setIsVisualTemplate] = useState(false);
  const [scale, setScale] = useState(1); // Add scale state for responsive design
  const { t } = useTranslation();
  const theme = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiUrlsoket = process.env.REACT_APP_API_URL_SOKET;
  const navigate = useNavigate();
  const [candidateId, setcandidateId] = useState();
  const [blockselectedCandidate, setBlockselectedCandidate] = useState();
  const recruiterId = getUserIdFromAccessToken();
  const [newStepDialog, setNewStepDialog] = useState({
    open: false,
    afterStep: null,
    newStepName: "",
  });

  // Alert dialog state
  const [deleteStepAlert, setDeleteStepAlert] = useState({
    open: false,
    stepName: "",
  });

  // Infinite scroll state for each column
  const [visibleCardsLimit, setVisibleCardsLimit] = useState({});
  const initialVisibleCards = 10;
  const loadMoreAmount = 10;

  // Handle scroll to load more cards
  const handleScroll = (e, columnId, totalCards) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    
    // Check if we are near the bottom (within 50px)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      const currentLimit = visibleCardsLimit[columnId] || initialVisibleCards;
      if (currentLimit < totalCards) {
        setVisibleCardsLimit(prev => ({
          ...prev,
          [columnId]: currentLimit + loadMoreAmount
        }));
      }
    }
  };


  const {
    signalingServer,
    signalingServer2,
    setSignalingServer,
    setSignalingServer2,
  } = useSignalingStore();

  useEffect(() => {
    console.log("test1");
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");
    console.log("test2");
    if (code) {
      console.log("The URL contains the code:", code);
      fetchCallbackData(code);
    } else {
      console.log("Code not found in the URL.");
    }
  }, []);

  useEffect(() => {
    if (selectedCandidate) {
      console.log("Selected Candidate status: ", selectedCandidate.status);
    }
  }, [selectedCandidate]);

  // Scroll to selected candidate in board view
  useEffect(() => {
    if (selectedCandidateId && boardCardRefs.current[selectedCandidateId]) {
      // Small delay to ensure DOM is ready and layout is stable
      setTimeout(() => {
        boardCardRefs.current[selectedCandidateId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }, 500);
    }
  }, [selectedCandidateId, candidates]);

  const fetchCallbackData = async (code) => {
    console.log("Code:", code);
    try {
      const response = await axios.post(
        `${apiUrl}authentication/linkedin-callback/`,
        {
          grant_type: "authorization_code",
          code: code,
          redirect_uri: `${window.location.origin}/talent/post/PostDetails`,
          client_id: "86o1ech9y4in4m",
          client_secret: "WPL_AP0.jW3gypWTWCnun6rz.MTIxMTM2NzA2NA==",
        }
      );
      console.log("LinkedIn token response:", response.data);

    } catch (error) {
      console.error("Error fetching data from callback:", error);
    }
  };

  useEffect(() => {
    console.log("those are the steps ", steps);
  }, []);
  useEffect(() => {
    console.log("test1");
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");
    console.log("test2");
    if (code) {
      console.log("The URL contains the code:", code);
      fetchCallbackData(code);
    } else {
      console.log("Code not found in the URL.");
    }
  }, []);

  useEffect(() => {
    if (candidates.length > 0) {
      console.log("Candidates lenght: ", candidates.length);
      console.log("Steps : ", steps);

      // Ensure standard steps are properly ordered
      const standardSteps = ["Application Declined", "Application Review", "Hired"];
      const orderedSteps = [];

      // Add Application Declined first
      if (steps.includes("Application Declined")) {
        orderedSteps.push("Application Declined");
      }

      // Add Application Review second
      if (steps.includes("Application Review")) {
        orderedSteps.push("Application Review");
      }

      // Add custom steps (excluding standard ones)
      const customSteps = steps.filter(step => !standardSteps.includes(step));
      orderedSteps.push(...customSteps);

      // Add Hired last
      if (steps.includes("Hired")) {
        orderedSteps.push("Hired");
      }

      const finalSteps = orderedSteps.length > 0 ? orderedSteps : steps;

      const columnsMap = finalSteps.reduce((acc, step) => {
        acc[step] = {
          id: step,
          title: step,
          cards: [],
        };
        return acc;
      }, {});

      candidates.forEach((candidate) => {
        const { step } = candidate;
        console.log("Candidate step2: ", step);
        console.log("columnsMap ", columnsMap[step]);
        console.log("Status ", candidate.status);
        if (columnsMap[step]) {
          if (
            candidate.status === "Application-Imported" ||
            candidate.status === "Scraping-Email" ||
            candidate.status === "Application Spontane"
          ) {
            columnsMap[step].cards.push({
              id: candidate.id,
              candidateId: candidate.id, // Use candidate.id for imported/scraped candidates
              title: candidate.extracted_informations?.Name || "Unknown Name",
              nom: candidate.extracted_informations?.Name || "",
              prenom: candidate.extracted_informations?.Name || "",
              description: candidate.extracted_informations?.Email || "",
              profile: candidate.extracted_informations?.Profil || "",
              experience: candidate.extracted_informations?.agexperiece || "",
              phone: candidate.score_matching || 0,
              status: candidate.status,
              step,
            });

          } else {
            columnsMap[step].cards.push({
              id: candidate.id,
              candidateId: candidate.candidate?.id,
              title: `${candidate.candidate?.last_name || ""} ${candidate.candidate?.first_name || ""}`.trim() || "Unknown Candidate",
              nom: candidate.candidate?.last_name || "",
              prenom: candidate.candidate?.first_name || "",
              description: candidate.extracted_informations?.Email || candidate.candidate?.email || "",
              profile: candidate.extracted_informations?.Profil || "",
              experience: candidate.extracted_informations?.agexperiece || "",
              phone: candidate.score_matching || 0,
              status: candidate.status,
              step,
            });
          }
        } else {
          console.warn(
            `Candidate ${candidate.id} has an invalid step: ${step}`
          );
        }
      });

      // Move selected candidate to the top of its column ONLY if it hasn't been forced yet
      if (selectedCandidateId && lastForcedId.current !== selectedCandidateId) {
        Object.values(columnsMap).forEach(column => {
          if (column && column.cards) {
            const index = column.cards.findIndex(c => c.id === selectedCandidateId);
            if (index > 0) {
              const [selectedCard] = column.cards.splice(index, 1);
              column.cards.unshift(selectedCard);
            }
          }
        });
        // Mark as forced so subsequent updates (like drag-and-drop) don't override the order
        lastForcedId.current = selectedCandidateId;
      } else if (!selectedCandidateId) {
        // Reset ref if no candidate is selected
        lastForcedId.current = null;
      }

      const columns = finalSteps.map((step) => columnsMap[step]).filter(Boolean);
      console.log("columns lenght: ", columns.length);

      // Initialize visible cards limit for each column
      const initialLimits = {};
      columns.forEach(column => {
        initialLimits[column.id] = initialVisibleCards;
      });
      setVisibleCardsLimit(initialLimits);
 
      setBoard({ columns });
    }
  }, [candidates, steps]);

  useEffect(() => {
    const infos = getDecodedToken();
    if (infos) {
      setUserId(infos.user_id);
    }
  }, []);
  useEffect(() => {
    const fetchAllNotes = async () => {
      if (candidates && candidates.length > 0) {
        const newNotes = {};
        for (const candidate of candidates) {
          await fetchNotesForCandidate(candidate.id, newNotes);
        }
        setNotes(newNotes);
      }
    };

    fetchAllNotes();
  }, [candidates]);

  useEffect(() => {
    fetchUserInfo();
  }, [userId]);
  useEffect(() => {
    if (selectedCandidate && selectedCandidate.email) {
      setEmailFormData((prev) => ({
        ...prev,
        recipient_email: selectedCandidate.email,
      }));
    }
  }, [selectedCandidate]);

  useEffect(() => {
    if (userId) {
      fetchEmailTemplates();
    }
  }, [userId]);

  useEffect(() => {
    if (open && userId) {
      fetchEmailTemplates();
    }
  }, [open, userId]);

  useEffect(() => {
    getTemplates();
  }, []);

  // Handle click outside template dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTemplateDropdown) {
        // Check if click is outside the template dropdown area
        const templateDropdown = document.querySelector(
          "[data-template-dropdown]"
        );
        if (templateDropdown && !templateDropdown.contains(event.target)) {
          setShowTemplateDropdown(false);
          setTemplateSearchValue("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTemplateDropdown]);

  // Add responsive scaling effect for dialog - comprehensive approach
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const contentHeight = 800; // Approximate height of your content
      const contentWidth = 1200; // Approximate width of your content

      // Calculate scale based on both height and width
      const heightScale = (windowHeight * 0.95) / contentHeight; // 95% of window height
      const widthScale = (windowWidth * 0.95) / contentWidth; // 95% of window width

      // Use the smaller scale to ensure content fits both dimensions
      let newScale = Math.min(heightScale, widthScale, 1);

      // Add minimum scale limit based on screen width
      if (windowWidth <= 1920) {
        // For 1080p and similar resolutions
        newScale = Math.max(newScale, 0.8); // Minimum 80% scale
      } else if (windowWidth <= 2560) {
        // For 1440p
        newScale = Math.max(newScale, 0.85); // Minimum 85% scale
      } else {
        newScale = Math.max(newScale, 0.9); // Minimum 90% scale for larger screens
      }

      setScale(newScale);
    };

    handleResize(); // Initial scale
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const postTemplate = async (title, message) => {
    console.log("title,message", title, message, userId);
    try {
      const response = await axios.post(
        `${apiUrl}posts/messagetemplates/recruiter/${recruiterId}/`,
        {
          title: title,
          message: message,
          recruiter: userId,
        }
      );
    } catch (err) {
      toast.error(t("board.post_registration_failed"));
    }
  };

  const addStep = (afterStep) => {
    setNewStepDialog({ open: true, afterStep, newStepName: "" });
    console.log("==>", setNewStepDialog.newStepName);
    // setSteps(setNewStepDialog.newStepName)
  };

  const handleAddNewStep = () => {
    const { afterStep, newStepName } = newStepDialog;
    const trimmedName = newStepName.trim();

    if (trimmedName === "") {
      toast.error(t("step_name_cannot_be_empty")); // Translated text
      return;
    }

    if (steps.includes(trimmedName)) {
      toast.error(t("step_name_must_be_unique")); // Translated text
      return;
    }

    setSteps((prevSteps) => {
      const index = prevSteps.indexOf(afterStep);
      if (index === -1) {
        toast.error(t("error_step_not_found")); // Translated text
        return prevSteps;
      }

      const newSteps = [...prevSteps];

      // If adding after "Hired", insert before "Hired" instead
      if (afterStep === "Hired") {
        newSteps.splice(index, 0, trimmedName);
      } else {
        // Check if "Hired" exists and if we're inserting after the second-to-last step
        const hiredIndex = newSteps.indexOf("Hired");
        if (hiredIndex !== -1 && index === hiredIndex - 1) {
          // Insert before "Hired" to keep "Hired" at the end
          newSteps.splice(hiredIndex, 0, trimmedName);
        } else {
          newSteps.splice(index + 1, 0, trimmedName);
        }
      }

      return newSteps;
    });

    setBoard((prevBoard) => {
      const index = prevBoard.columns.findIndex(
        (column) => column.id === afterStep
      );
      if (index === -1) {
        toast.error(t("error_step_not_found")); // Translated text
        return prevBoard;
      }

      const newColumns = [...prevBoard.columns];
      const newColumn = {
        id: trimmedName,
        title: trimmedName,
        cards: [],
      };

      // If adding after "Hired", insert before "Hired" instead
      if (afterStep === "Hired") {
        newColumns.splice(index, 0, newColumn);
      } else {
        // Check if "Hired" column exists and if we're inserting after the second-to-last column
        const hiredColumnIndex = newColumns.findIndex(col => col.id === "Hired");
        if (hiredColumnIndex !== -1 && index === hiredColumnIndex - 1) {
          // Insert before "Hired" to keep "Hired" at the end
          newColumns.splice(hiredColumnIndex, 0, newColumn);
        } else {
          newColumns.splice(index + 1, 0, newColumn);
        }
      }

      createStep(trimmedName);
      return { columns: newColumns };
    });

    toast.success(t("new_step_added_successfully")); // Translated text
    setNewStepDialog({ open: false, afterStep: null, newStepName: "" });
  };

  const removeStep = (stepToRemove) => {
    if (steps.length > 1) {
      const stepToRemoveColumn = controlledBoard.columns.find(
        (col) => col.id === stepToRemove
      );
      if (stepToRemoveColumn && stepToRemoveColumn.cards.length > 0) {
        toast.warning(t("cannot_remove_step")); // Translated text
        return;
      }
      deleteSteps(stepToRemove);

      const newSteps = steps.filter((step) => step !== stepToRemove);
      setSteps(newSteps);

      setBoard((prevBoard) => ({
        columns: prevBoard.columns.filter(
          (column) => column.id !== stepToRemove
        ),
      }));
    } else {
      toast.warning(t("cannot_remove_last_step")); // Translated text
    }
  };

  // Handle delete step confirmation
  const handleDeleteStepConfirm = () => {
    const { stepName } = deleteStepAlert;
    removeStep(stepName);
    console.log("Deleting step:", stepName);
    setDeleteStepAlert({ open: false, stepName: "" });
  };

  // Handle delete step cancel
  const handleDeleteStepCancel = () => {
    setDeleteStepAlert({ open: false, stepName: "" });
  };
  const getTemplates = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}posts/messagetemplates/recruiter/${recruiterId}/`
      );
      console.log("Templates fetched:", response.data);
      setTemplates(response.data);
      return response.data;
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      throw err;
    }
  };

  useEffect(() => {

    if (blockselectedCandidate) {
      console.log(
        "candidate is :",
        blockselectedCandidate.firstName,
        "---",
        blockselectedCandidate.candidateId,
        "---",
        recruiterId
      );

    }
  }, [blockselectedCandidate]);
  const updateTemplate = async (templateId, title, message) => {
    try {
      const response = await axios.put(
        `${apiUrl}posts/messagetemplates/recruiter/${recruiterId}/`,
        {
          id: templateId,
          title: title,
          message: message,
          recruiter: recruiterId,
        }
      );
      console.log("Template updated:", response.data);
      return response.data;
    } catch (err) {
      console.error("Failed to update template:", err);
      throw err;
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const response = await axios.delete(
        `${apiUrl}posts/messagetemplates/recruiter/${userId}/`,
        {
          data: { id: templateId },
        }
      );
      console.log("Template deleted");
      return response.data;
    } catch (err) {
      console.error("Failed to delete template:", err);
      throw err;
    }
  };

  const fetchUserInfo = async () => {
    if (userId) {
      try {
        const response = await fetch(`${apiUrl}recruiters/${userId}/find/`, {
          headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        });

        if (response.status === 401) {
          console.log("Unauthorized. Redirecting to login page...");
          navigate("/login");
          return;
        }

        const json = await response.json();
        setUserId(json.id);
        setRecruiterName(`${json.last_name} ${json.first_name}`);
        setRecruiterEmail(json.email);
        setCompanyName(json.company);
        setRecruiterPhone(json.phone);
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    }
  };






  const fetchEmailTemplates = async (page = 1, append = false) => {
    try {
      setLoadingTemplates(true);
      const response = await axios.get(
        `${apiUrl}recruiters/${userId}/templates/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: {
            page: page,
            page_size: 20,
          },
        }
      );

      if (response.data && response.data.results) {
        if (append) {
          setAvailableTemplates((prev) => [...prev, ...response.data.results]);
        } else {
          setAvailableTemplates(response.data.results);
        }
      } else {
        setAvailableTemplates(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching email templates:", error);
      setAvailableTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };
  const handleEmailTemplateChange = (event) => {
    const value = event.target.value;
    setSelectedTemplateId(value || "");

    if (value) {
      const template = availableTemplates.find((t) => t.id === value);
      if (template) {
        setSelectedEmailTemplate(template);
        setIsVisualTemplate(!!template.design);


        const uniquePlaceholders = new Set();
        const templateText = `${template.subject} ${template.body}`;
        const templateIsVisual = !!template.design;

        if (templateIsVisual) {

          const visualMatches = templateText.match(
            /\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g
          );
          if (visualMatches) {
            visualMatches.forEach((match) => {
              const placeholderName = match.slice(2, -2);
              uniquePlaceholders.add(placeholderName);
            });
          }
        } else {

          const standardMatches = templateText.match(
            /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g
          );
          if (standardMatches) {
            standardMatches.forEach((match) => {
              const placeholderName = match.slice(1, -1);
              uniquePlaceholders.add(placeholderName);
            });
          }
        }

        console.log(
          "Template:",
          template.name,
          "IsVisual:",
          templateIsVisual,
          "Placeholders found:",
          [...uniquePlaceholders]
        );

        setEmailPlaceholders([...uniquePlaceholders]);


        const initialPlaceholderValues = {};
        uniquePlaceholders.forEach((placeholder) => {
          if (selectedCandidate) {
            switch (placeholder) {
              case "first_name":
              case "prénom":
                initialPlaceholderValues[placeholder] =
                  selectedCandidate.firstName || "";
                break;
              case "last_name":
              case "nom_famille":
                initialPlaceholderValues[placeholder] =
                  selectedCandidate.lastName || "";
                break;
              case "nom_complet":
                initialPlaceholderValues[placeholder] = `${selectedCandidate.firstName || ""
                  } ${selectedCandidate.lastName || ""}`.trim();
                break;
              case "company":
              case "entreprise":
                initialPlaceholderValues[placeholder] =
                  post?.company || companyName || "";
                break;
              case "date":
                initialPlaceholderValues[placeholder] = eventDate
                  ? eventDate.format("MMMM D, YYYY h:mm A")
                  : "";
                break;
              case "time":
              case "heure":
                initialPlaceholderValues[placeholder] = eventDate
                  ? eventDate.format("h:mm A")
                  : "";
                break;
              case "location":
              case "lieu":
                initialPlaceholderValues[placeholder] =
                  post?.localisation || "";
                break;
              case "Subject":
              case "Sujet":
                const company = post?.company || companyName || "";
                const position = post?.title || "";
                const interviewDate = eventDate
                  ? eventDate.format("MMMM D, YYYY")
                  : "";
                initialPlaceholderValues[placeholder] = t(
                  "board.interview_invitation_subject",
                  { company, position, interviewDate }
                );
                break;
              case "position":
              case "poste":
                initialPlaceholderValues[placeholder] = post?.title || "";
                break;
              case "job_title":
                initialPlaceholderValues[placeholder] = post?.title || "";
                break;
              case "salary":
                initialPlaceholderValues[placeholder] = post?.salary
                  ? `${post.salary} ${post.currency || ""}`.trim()
                  : "";
                break;
              case "deadline":
                initialPlaceholderValues[placeholder] = post?.deadline || "";
                break;
              case "work_mode":
                initialPlaceholderValues[placeholder] = post?.mode || "";
                break;
              case "job_description":
                initialPlaceholderValues[placeholder] = post?.description || "";
                break;
              case "requirements":
                initialPlaceholderValues[placeholder] = Array.isArray(
                  post?.requirements
                )
                  ? post.requirements.join(", ")
                  : post?.requirements || "";
                break;
              default:
                initialPlaceholderValues[placeholder] = "";
            }
          } else {
            initialPlaceholderValues[placeholder] = "";
          }
        });

        setEmailFormData((prev) => ({
          ...prev,
          placeholder_values: initialPlaceholderValues,
        }));


      }
    } else {
      setSelectedEmailTemplate(null);
      setIsVisualTemplate(false);
      setEmailPlaceholders([]);
      setEmailFormData((prev) => ({
        ...prev,
        placeholder_values: {},
      }));
      setRenderedEmail({
        subject: "",
        body: "",
      });
    }
  };


  useEffect(() => {
    if (selectedEmailTemplate && emailFormData.placeholder_values) {
      const replacePlaceholders = (
        text,
        placeholderValues,
        isVisual = false
      ) => {
        if (!text) return "";

        let result = text;


        Object.entries(placeholderValues).forEach(([key, value]) => {
          if (isVisual) {
            const visualRegex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
            result = result.replace(visualRegex, value || `{{${key}}}`);
          } else {
            const standardRegex = new RegExp(`\\{${key}\\}`, "g");
            result = result.replace(standardRegex, value || `{${key}}`);
          }
        });

        return result;
      };


      const updatedSubject = replacePlaceholders(
        selectedEmailTemplate.subject,
        emailFormData.placeholder_values,
        isVisualTemplate
      );

      const updatedBody = replacePlaceholders(
        selectedEmailTemplate.body,
        emailFormData.placeholder_values,
        isVisualTemplate
      );

      setRenderedEmail({
        subject: updatedSubject,
        body: updatedBody,
      });
    }
  }, [
    selectedEmailTemplate,
    emailFormData.placeholder_values,
    isVisualTemplate,
  ]);


  const handleEmailFormChange = (e) => {
    const { name, value } = e.target;
    setEmailFormData((prev) => ({ ...prev, [name]: value }));


    if (emailErrors[name]) {
      setEmailErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };


  const handleEmailPlaceholderChange = (placeholder, value) => {
    const updatedPlaceholderValues = {
      ...emailFormData.placeholder_values,
      [placeholder]: value,
    };

    setEmailFormData((prev) => ({
      ...prev,
      placeholder_values: updatedPlaceholderValues,
    }));

    if (emailErrors[`placeholder_${placeholder}`]) {
      setEmailErrors((prev) => ({
        ...prev,
        [`placeholder_${placeholder}`]: "",
      }));
    }
  };


  const handleSendEmail = async () => {
    try {
      setEmailLoading(true);
      setEmailErrors({});


      const errors = {};
      if (!emailFormData.recipient_email) {
        errors.recipient_email = t("board.recipient_email_required");
      }
      if (!selectedEmailTemplate) {
        errors.template = t("board.please_select_template");
      }

      if (Object.keys(errors).length > 0) {
        setEmailErrors(errors);
        return;
      }

      const payload = {
        template_id: selectedEmailTemplate.id,
        recipient_email: emailFormData.recipient_email,
        placeholder_values: emailFormData.placeholder_values,
        is_visual_template: isVisualTemplate,
      };

      const response = await axios.post(
        `${apiUrl}recruiters/${userId}/send-email/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      setEmailStatus({
        type: "success",
        message: t("board.email_sent_successfully"),
      });


      setTimeout(() => {
        setIsEmailInterfaceVisible(false);
        setSelectedTemplateId("");
        setSelectedEmailTemplate(null);
        setEmailFormData({
          recipient_email: "",
          placeholder_values: {},
        });
        setEmailStatus({ type: null, message: "" });
      }, 2000);
    } catch (error) {
      console.error("Email sending failed:", error);
      setEmailStatus({
        type: "error",
        message:
          error.response?.data?.detail || t("board.failed_to_send_email"),
      });
    } finally {
      setEmailLoading(false);
    }
  };


  const handleSendEmailAfterEvent = async () => {
    try {

      if (!emailFormData.recipient_email || !selectedEmailTemplate) {
        console.log("Email not sent: Missing recipient email or template");
        return;
      }

      const payload = {
        template_id: selectedEmailTemplate.id,
        recipient_email: emailFormData.recipient_email,
        placeholder_values: emailFormData.placeholder_values,
        is_visual_template: isVisualTemplate,
      };

      console.log("Sending email after event creation with payload:", payload);

      const response = await axios.post(
        `${apiUrl}recruiters/${userId}/send-email/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Email sent successfully after event creation");


      setEmailStatus({
        type: "success",
        message: t("board.event_created_email_sent_successfully"),
      });
    } catch (error) {
      console.error("Email sending failed after event creation:", error);
      setEmailStatus({
        type: "error",
        message:
          t("board.event_created_email_failed") +
          ": " +
          (error.response?.data?.detail || t("board.unknown_error")),
      });
    }
  };

  const handleSubmit = async (event) => {

    if (event) event.preventDefault();

    console.log("handleSubmit called");
    setCreating(true);

    const eventData = {
      candidate_id: selectedCandidate.candidateId,
      recruiter: userId,
      event_title: eventTitle,
      event_start_date: eventDate,
    };

    try {

      const response = await axios.post(
        `${apiUrl}authentication/events/`,
        eventData
      );
      console.log("Event created successfully:", response.data);


      sendWebSocketMessage(
        selectedCandidate.candidateId,
        eventDate.format("YYYY-MM-DD HH:mm:ss")
      );


      if (selectedEmailTemplate) {
        console.log("Automatically sending email after event creation...");
        await handleSendEmailAfterEvent();
      }

      console.log("After successful event creation");
      handleDialogClose();
    } catch (error) {
      console.error("There was an error creating the event!", error);
    } finally {
      setCreating(false);
    }
  };
  const contact = async () => {
    console.log("contact called");

    // Check if template is required (when event section is hidden)
    const isEventSectionHidden = selectedCandidate &&
      (selectedCandidate.status === "Application-Imported" ||
        selectedCandidate.status === "Application Spontane" ||
        selectedCandidate.status === "Scraping-Email");

    if (isEventSectionHidden && !selectedEmailTemplate) {
      setEmailErrors({ template: t("board.please_select_template") });
      return;
    }

    if (selectedEmailTemplate) {
      await handleSendEmailAfterEvent();
    }
    handleDialogClose();
  };

  const createBlacklistEntry = async (recruiterId, candidateId, reason) => {
    try {
      console.log("Creating blacklist entry: ", {
        recruiterId,
        candidateId,
        reason,
      });

      const response = await fetch(
        `${apiUrl}authentication/blacklist/create/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            recruiter_id: recruiterId,
            candidate_id: candidateId,
            reason: reason,
          }),
        }
      );


      const data = await response.json();

      if (response.ok) {
        toast.success(t("blacklist_created_success") || "Candidate blacklisted successfully");
        setShowBlockConfirmation(false);
        setReason("");
        // Remove candidate from board without page refresh
        setBoard(prevBoard => ({
          columns: prevBoard.columns.map(column => ({
            ...column,
            cards: column.cards.filter(card => card.candidateId !== candidateId)
          }))
        }));
        return data;
      } else {
        console.error("Error creating blacklist entry:", data);
        toast.error(data.message || data.detail || t("blacklist_created_error") || "Error creating blacklist entry");
        return data;
      }
    } catch (error) {
      console.error("Error creating blacklist entry:", error);
      toast.error(t("blacklist_created_error") || "Error creating blacklist entry");
      throw error;
    }
  };
  // Old sendMessage function removed - now using embedded email interface

  function getDecodedToken() {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      console.error("No access token found in local storage.");
      return null;
    }

    function decodeJWT(token) {
      const parts = token.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid token format.");
      }
      const payload = parts[1];
      const decodedPayload = atob(payload);
      const decodedData = JSON.parse(decodedPayload);
      return decodedData;
    }

    try {
      return decodeJWT(token);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }
  const [lastPositoninCart, setLastPositioninCart] = useState();

  function handleCardMove(_card, source, destination) {
    const updatedBoard = moveCard(controlledBoard, source, destination);
    setBoard(updatedBoard);
    // setCount((prevCount) => prevCount + 1);
    setInterviewType(destination.toColumnId);

    if (onUpdateStep) {
      onUpdateStep(
        _card.id,
        destination.toColumnId,
        _card.status,
        _card.description
      );
      sendnotif(_card, destination.toColumnId);
    }
  }
  const sendnotif = (card, newStep) => {
    const receiverId = card.candidateId;
    console.log(
      "Connecting to signaling server: ",
      signalingServer ? "Connected" : "Not connected"
    );

    // Check if signaling server is not connected or closed
    if (!signalingServer || signalingServer.readyState !== WebSocket.OPEN) {
      console.log("WebSocket is not connected. Attempting to reconnect...");

      // Note: WebSocket connections are now managed by WebSocketManager
      console.log(
        "WebSocket not connected - relying on WebSocketManager for connection management"
      );
      return false;
    } else {
      // If already connected, send the notification immediately
      return sendNotificationMessage(
        signalingServer,
        card,
        newStep,
        receiverId,
        userId
      );
    }
  };

  // Helper function to send the actual notification message
  const sendNotificationMessage = (
    server,
    card,
    newStep,
    receiverId,
    userId
  ) => {
    const timestamp = new Date().toISOString();
    const messageData = {
      type: "notif_message",
      notif: t("board.application_status_updated", {
        postTitle: post.title,
        newStep,
      }),
      timestamp,
      link: "/post_applied",
      sender_id: userId,
      receiver_id: receiverId,
    };

    try {
      server.send(JSON.stringify(messageData));
      console.log("Notification sent successfully");
      return true;
    } catch (error) {
      console.error("Error sending notification:", error);
      return false;
    }
  };
  const sendWebSocketMessage = (candidateId, eventDateTime) => {
    const { signalingServer2 } = useSignalingStore.getState();

    if (signalingServer2 && signalingServer2.readyState === WebSocket.OPEN) {
      const messageData = {
        type: "chat_message",
        message: t("board.meeting_scheduled_for", { eventDateTime }),
        timestamp: new Date().toISOString(),
        sender_id: userId,
        receiver_id: candidateId,
      };

      try {
        signalingServer2.send(JSON.stringify(messageData));
        console.log("WebSocket message sent successfully");
        // navigate(0);
      } catch (error) {
        console.error("Error sending WebSocket message:", error);
      }
    } else {
      console.log("WebSocket is not connected. Cannot send message.");
      // Note: WebSocket connections are now managed by WebSocketManager
      console.log("Relying on WebSocketManager for connection management");
    }
  };

  function handleDialogClose() {
    setOpen(false);
    // Reset email form when dialog closes
    setSelectedTemplateId("");
    setSelectedEmailTemplate(null);
    setEmailFormData({
      recipient_email: "",
      placeholder_values: {},
    });
    setEmailStatus({ type: null, message: "" });
    setEmailErrors({});
  }
  const handleCloseAlert = () => {
    setOpenalert(false);
  };
  const [hoveredNote, setHoveredNote] = useState(null);
  const [noteDetailsAnchor, setNoteDetailsAnchor] = useState(null);
  // const showNoteDetails = (note, event) => {
  //   setHoveredNote(note);
  //   setNoteDetailsAnchor(event.currentTarget);
  // };

  // const hideNoteDetails = () => {
  //   setHoveredNote(null);
  //   setNoteDetailsAnchor(null);
  // };
  const getCurrentStep = (cardId) => {
    for (const column of controlledBoard.columns) {
      const card = column.cards.find((c) => c.id === cardId);
      if (card) {
        return column.id; // This is the step (column title)
      }
    }
    return null;
  };
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [openalert, setOpenalert] = useState(false);
  function renderCard(card, { dragging }) {
    const isSelected = selectedCandidateId === card.id;
    return (
      <Grid 
        container 
        item 
        xs={12} 
        sx={{ padding: "2px" }} 
        ref={(el) => (boardCardRefs.current[card.id] = el)}
      >
        <Paper
          elevation={dragging ? 4 : 0}
          onClick={(e) => {
            // Only trigger navigation if not clicking on action buttons
            if (!e.target.closest('.action-buttons') && !e.target.closest('button') && onCardClick) {
              onCardClick(card.id);
            }
          }}
          sx={{
            p: 0.85, // Reduced from 1.25
            width: "100%",
            borderRadius: "10px",
            backgroundColor: dragging 
              ? "#f8faff" 
              : isSelected 
                ? "rgba(0, 23, 231, 0.05)" 
                : "#fff",
            border: dragging 
              ? "1px solid #0017E7" 
              : isSelected 
                ? "2px solid #0017E7" 
                : "1px solid #e2e8f0",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            flexDirection: "column",
            gap: 0.4, // Further reduced from 0.75 to eliminate extra space
            overflow: "visible", // Ensure action menu doesn't get clipped
            position: "relative",
            cursor: "grab",
            "&:hover": {
              zIndex: 300, // Bring hovered card to front for action menu
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              borderColor: "#0017E7",
              transform: dragging ? "none" : "translateY(-2px)",
            },
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          {/* Main Info Header: Name, Score, Profile/Exp */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.1, maxWidth: "calc(100% - 45px)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 700,
                    fontSize: "11px", // Slightly reduced
                    color: "#1a202c",
                    lineHeight: 1.1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {card.title}
                </Typography>
                {/* Years of Experience (Relocated to right) */}
                {card.experience && (
                  <Tooltip title="Experience" arrow placement="top">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.3,
                        bgcolor: "#f7fafc",
                        px: 0.4,
                        py: 0.1,
                        borderRadius: "3px",
                        border: "1px solid #edf2f7",
                        flexShrink: 0,
                        cursor: "default",
                      }}
                    >
                      <WorkOutlineIcon sx={{ fontSize: 8.5, color: "#718096", opacity: 0.8 }} />
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "7.5px",
                          color: "#718096",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {card.experience}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
              
              {card.profile && (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.4, mt: 0.1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "8.5px", // Slightly reduced
                      color: "#0017E7",
                      fontWeight: 600,
                      backgroundColor: "rgba(0, 23, 231, 0.08)",
                      px: 0.4,
                      borderRadius: "3px",
                    }}
                  >
                    {card.profile}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Score Section */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
              {/* AI Score */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: card.phone > 70 ? "#e6fffa" : "#fffaf0",
                  borderRadius: "6px",
                  px: 0.6,
                  py: 0.2,
                  gap: 0.4,
                }}
              >
                <img src={ai_icon} style={{ width: 9, height: 9 }} />
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "8px",
                      color: card.phone > 70 ? "#2c7a7b" : "#b7791f",
                      fontWeight: 700,
                    }}
                  >
                    {card.phone}%
                  </Typography>
              </Box>
            </Box>
          </Box>

          {/* Contact Line & Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, position: "relative", mt: 0 }}>
            <Tooltip title={card.description} arrow placement="top">
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "9.5px", // Further reduced from 10.5px
                  color: "#718096",
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "calc(100% - 30px)",
                  cursor: "default",
                }}
              >
                {card.description}
              </Typography>
            </Tooltip>

            {/* Animated Action Menu */}
            <ActionMenuContainer onClick={(e) => e.stopPropagation()}>
              <Box className="action-buttons">
                {/* Dynamic Action Buttons to avoid gaps when one is hidden */}
                {[
                  {
                    key: "schedule",
                    component: (
                      <ActionButton
                        title="Schedule Meeting"
                        bgcolor="#00D2B1" // Stronger Mint/Teal
                        textcolor="#fff"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(true);
                          setSelectedCandidate({
                            id: card.id,
                            candidateId: card.candidateId,
                            firstName: card.prenom,
                            lastName: card.nom,
                            email: card.description,
                            phone: card.phone,
                            step: card.step,
                            status: card.status,
                          });
                          setInterviewType(card.step);
                          setcandidateId(card.candidateId);
                        }}
                      >
                        <img src={ring_icon} alt="schedule" />
                      </ActionButton>
                    )
                  },
                  {
                    key: "note",
                    component: (
                      <ActionButton
                        title="Add Note"
                        bgcolor="#FFB302" // Stronger Yellow/Gold
                        textcolor="#fff"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewAllNotes(card.id);
                        }}
                      >
                        <NoteAddIcon />
                      </ActionButton>
                    )
                  },
                  {
                    key: "block",
                    condition: card.status !== "Application-Imported" && 
                               card.status !== "Application Spontane" && 
                               card.status !== "Scraping-Email",
                    component: (
                      <ActionButton
                        title="Block Candidate"
                        bgcolor="#FF4D4D" // Stronger Red
                        textcolor="#fff"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCandidate({
                            id: card.id,
                            candidateId: card.candidateId,
                            firstName: card.prenom,
                            lastName: card.nom,
                          });
                          setShowBlockConfirmation(true);
                        }}
                      >
                        <BlockIcon />
                      </ActionButton>
                    )
                  },
                  {
                    key: "history",
                    component: (
                      <ActionButton
                        title="User History"
                        bgcolor="#4A5568" // Sleek Dark Grey
                        textcolor="#fff"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCandidate({
                            id: card.id,
                            candidateId: card.candidateId,
                            firstName: card.prenom,
                            lastName: card.nom,
                            email: card.description,
                          });
                          setIsEmailHistoryOpen(true);
                        }}
                      >
                        <HistoryIcon />
                      </ActionButton>
                    )
                  }
                ]
                .filter(item => item.condition !== false)
                .map((item, index) => (
                  <Box key={item.key} className={`action-item item-${index + 1}`}>
                    {item.component}
                  </Box>
                ))}
              </Box>
              <SettingsIcon className="settings-trigger" sx={{ fontSize: 15 }} />
            </ActionMenuContainer>
          </Box>

          {/* Simplified Block Dialog */}
          <Dialog
            open={showBlockConfirmation && selectedCandidate?.id === card.id}
            onClose={() => setShowBlockConfirmation(false)}
            onClick={(e) => e.stopPropagation()}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
             <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                Block Candidate
             </DialogTitle>
             <DialogContent>
               <Typography variant="body2" sx={{ mb: 2, color: "#4a5568" }}>
                  Are you sure you want to block <strong>{card.title}</strong>? They will be removed from all pipelines.
               </Typography>
               <TextField
                 autoFocus
                 fullWidth
                 size="small"
                 label="Reason (optional)"
                 multiline
                 rows={2}
                 value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 sx={{ mt: 1 }}
               />
             </DialogContent>
             <DialogActions sx={{ p: 2, pt: 0 }}>
               <Button onClick={() => setShowBlockConfirmation(false)} size="small" variant="text" color="inherit">
                 Cancel
               </Button>
               <Button 
                 onClick={() => createBlacklistEntry(recruiterId, card.candidateId, reason)}
                 size="small" 
                 variant="contained" 
                 color="error"
                 sx={{ borderRadius: "8px", textTransform: "none" }}
               >
                 Confirm Block
               </Button>
             </DialogActions>
          </Dialog>
        </Paper>
      </Grid>
    );
  }

  // Update rendered email when form data changes (same logic as SendEmailModal)
  useEffect(() => {
    if (selectedEmailTemplate && emailFormData.placeholder_values) {
      let renderedSubject = selectedEmailTemplate.subject;
      let renderedBody = selectedEmailTemplate.body;
      const templateIsVisual = !!selectedEmailTemplate.design;

      // Replace placeholders with values based on template type
      Object.entries(emailFormData.placeholder_values).forEach(
        ([key, value]) => {
          const placeholder = templateIsVisual ? `{{${key}}}` : `{${key}}`;
          const escapedPlaceholder = placeholder.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
          );

          renderedSubject = renderedSubject.replace(
            new RegExp(escapedPlaceholder, "g"),
            value || placeholder
          );

          renderedBody = renderedBody.replace(
            new RegExp(escapedPlaceholder, "g"),
            value || placeholder
          );
        }
      );

      setRenderedEmail({
        subject: renderedSubject,
        body: renderedBody,
      });
    }
  }, [selectedEmailTemplate, emailFormData.placeholder_values]);

  // Update date and time placeholders when eventDate changes
  useEffect(() => {
    if (selectedEmailTemplate && eventDate && emailPlaceholders.length > 0) {
      const updatedPlaceholderValues = { ...emailFormData.placeholder_values };
      let hasDateTimeUpdates = false;

      // Update date placeholder if it exists
      if (emailPlaceholders.includes("date")) {
        updatedPlaceholderValues.date = eventDate.format("MMMM D, YYYY h:mm A");
        hasDateTimeUpdates = true;
      }

      // Update time placeholder if it exists
      if (emailPlaceholders.includes("time")) {
        updatedPlaceholderValues.time = eventDate.format("h:mm A");
        hasDateTimeUpdates = true;
      }

      // Only update if we actually have date/time placeholders
      if (hasDateTimeUpdates) {
        setEmailFormData((prev) => ({
          ...prev,
          placeholder_values: updatedPlaceholderValues,
        }));
      }
    }
  }, [eventDate, selectedEmailTemplate, emailPlaceholders]);

  useEffect(() => {
    if (selectedCandidate) {
      fetchNotes(selectedCandidate.id);
    }
  }, [selectedCandidate]);

  const [notes, setNotes] = useState({});
  const [newNotes, setNewNotes] = useState({});
  useEffect(() => {
    const fetchAllNotes = async () => {
      if (candidates && candidates.length > 0) {
        const newNotes = {};
        for (const candidate of candidates) {
          await fetchNotesForCandidate(candidate.id, newNotes);
        }
        setNotes(newNotes);
        console.log("Notes:", newNotes);
      }
    };

    fetchAllNotes();
  }, [candidates]);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleNoteHover = useCallback((note, event) => {
    setHoveredNote(note);
    setAnchorEl(event.currentTarget);
  }, []);

  const handleNoteLeave = useCallback(() => {
    setHoveredNote(null);
    setAnchorEl(null);
  }, []);

  const fetchNotesForCandidate = async (candidateId, notesObj = null) => {
    if (candidateId) {
      try {
        const storedUserId =
          localStorage.getItem("userId") || getUserIdFromAccessToken();

        if (!storedUserId) {
          console.error("User ID is not available");
          return;
        }

        const response = await axios.get(
          `${apiUrl}messaging/applications/${candidateId}/notes/${storedUserId}/`,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken"),
            },
          }
        );

        if (notesObj) {
          notesObj[candidateId] = response.data;
        } else {
          setNotes((prevNotes) => ({
            ...prevNotes,
            [candidateId]: response.data,
          }));
        }
      } catch (error) {
        console.error(
          `Error fetching notes for candidate ${candidateId}:`,
          error
        );
      }
    }
  };
  const fetchNotes = async (applicationId) => {
    try {
      const response = await axios.get(
        `${apiUrl}messaging/applications/${applicationId}/notes/`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        }
      );
      setNotes((prevNotes) => ({
        ...prevNotes,
        [applicationId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const addNote = async (applicationId) => {
    let cardToAddNoteTo = null;
    for (const column of controlledBoard.columns) {
      cardToAddNoteTo = column.cards.find((card) => card.id === applicationId);
      if (cardToAddNoteTo) break;
    }

    if (!cardToAddNoteTo) {
      console.error("Cannot add note: Card not found");
      return;
    }

    const storedUserId =
      localStorage.getItem("userId") || getUserIdFromAccessToken();

    if (!storedUserId) {
      console.error("User ID is not available");
      return;
    }

    try {
      const response = await axios.post(
        `${apiUrl}messaging/applications/${applicationId}/notes/${storedUserId}/`,
        {
          note: newNotes[applicationId] || "",
          application: applicationId,
          recruiter: storedUserId,
          step: cardToAddNoteTo.step,
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        }
      );
      setNotes((prevNotes) => ({
        ...prevNotes,
        [applicationId]: [...(prevNotes[applicationId] || []), response.data],
      }));
      setNewNotes((prevNewNotes) => ({
        ...prevNewNotes,
        [applicationId]: "",
      }));
    } catch (error) {
      console.error("Error adding note:", error);
      if (error.response) {
        console.error("Server response:", error.response.data);
      }
    }
  };
  const deleteNote = async (applicationId, noteId) => {
    const storedUserId =
      localStorage.getItem("userId") || getUserIdFromAccessToken();

    if (!storedUserId) {
      console.error("User ID is not available");
      return;
    }

    try {
      await axios.delete(
        `${apiUrl}messaging/applications/${applicationId}/notes/${storedUserId}/${noteId}/`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        }
      );
      setNotes((prevNotes) => ({
        ...prevNotes,
        [applicationId]: prevNotes[applicationId].filter(
          (note) => note.id !== noteId
        ),
      }));
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };
  // Note: WebSocket connections are now managed by WebSocketManager component
  // Removed duplicate WebSocket creation logic to prevent infinite connection loops

  // Note: WebSocket initialization is now handled by WebSocketManager component

  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      //console.log("Access token : ", decodedToken);
      const userId = decodedToken.user_id;
      return userId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }
  function getUserRoleFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      //console.log("Access token : ", decodedToken);
      const userRole = decodedToken.role;
      return userRole;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }
  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    //console.log("payload : ", jsonPayload);

    return JSON.parse(jsonPayload);
  }

  const [noteDetailsOpen, setNoteDetailsOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [allNotesOpen, setAllNotesOpen] = useState(false);
  const [currentCardId, setCurrentCardId] = useState(null);
  const [hoverTimer, setHoverTimer] = useState(null);
  const showNoteDetails = (note, event) => {
    setHoveredNote(note);
  };

  const hideNoteDetails = () => {
    setHoveredNote(null);
  };

  const handleNoteClick = (note) => {
    setSelectedNote(note);
    setNoteDetailsOpen(true);
  };

  const handleViewAllNotes = (cardId) => {
    setCurrentCardId(cardId);
    fetchNotesForCandidate(cardId);
    setAllNotesOpen(true);
  };

  const handleCloseNoteDetails = () => {
    setNoteDetailsOpen(false);
    setSelectedNote(null);
  };

  const handleCloseAllNotes = () => {
    setAllNotesOpen(false);
    setCurrentCardId(null);
  };
  console.log("Popover state:", {
    open: Boolean(noteDetailsAnchor),
    anchorEl: noteDetailsAnchor,
    hoveredNote,
  });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    if (noteDetailsAnchor) {
      setIsPopoverOpen(true);
    } else {
      const timer = setTimeout(() => setIsPopoverOpen(false), 300);
      return () => clearTimeout(timer);
    }
  }, [noteDetailsAnchor]);

  // Old handleSelectChange function removed - now using handleEmailTemplateChange for embedded interface

  const handleNewTemplateChange = (event) => {
    const { name, value } = event.target;
    setNewTemplate((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTemplate = () => {
    if (newTemplate.name && newTemplate.content) {
      setTemplates((prev) => [...prev, { id: Date.now(), ...newTemplate }]);
      setNewTemplate({ name: "", content: "" });
      postTemplate(newTemplate.name, newTemplate.content);
    }
  };

  const handleEditTemplate = (id) => {
    setEditingTemplateId(id);
    const template = templates.find((t) => t.id === id);
    if (template) {
      setNewTemplate({ name: template.title, content: template.message });
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      await updateTemplate(
        editingTemplateId,
        newTemplate.name,
        newTemplate.content
      );

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === editingTemplateId
            ? { ...t, title: newTemplate.name, message: newTemplate.content }
            : t
        )
      );

      setEditingTemplateId(null);
      setNewTemplate({ name: "", content: "" });
      setOpenTemplate(false);

      toast.success("Template updated successfully");
    } catch (error) {
      console.error("Failed to update template:", error);
      toast.error("Failed to update template");
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await deleteTemplate(id);

      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (selectedTemplateId === id) {
        setSelectedTemplateId("");
      }

      toast.success("Template deleted successfully");
    } catch (error) {
      console.error("Failed to delete template:", error);
      toast.error("Failed to delete template");
    }
  };

  // Dynamic responsive scaling state
  const [dropdownScale, setDropdownScale] = useState(1);

  // Dynamic responsive scaling effect
  useEffect(() => {
    const updateDropdownScale = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Calculate scale based on 95% of window dimensions with minimum limits
      const widthScale = Math.max(0.3, (windowWidth * 0.95) / 1200); // Min 30% scale
      const heightScale = Math.max(0.3, (windowHeight * 0.95) / 800); // Min 30% scale

      // Use the smaller scale to ensure it fits in both dimensions
      const finalScale = Math.min(widthScale, heightScale, 1); // Max 100% scale

      setDropdownScale(finalScale);
    };

    // Initial calculation
    updateDropdownScale();

    // Add resize listener
    window.addEventListener("resize", updateDropdownScale);

    // Cleanup
    return () => window.removeEventListener("resize", updateDropdownScale);
  }, []);

  const StyledSelect = styled(Select)(({ theme }) => ({
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.mode === "light" ? "#E0E3E7" : "#2D3843",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  }));

  const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }));
  return (
    <>
      {isEmailHistoryOpen && selectedCandidate && (
        <EmailHistory
          open={isEmailHistoryOpen}
          onClose={() => setIsEmailHistoryOpen(false)}
          candidateEmail={selectedCandidate.email}
          recruiterId={recruiterId}
        />
      )}
      <style>{`
        .react-kanban-column {
          background: #fff !important;
          border-radius: 12px !important;
          padding: 10px !important; // Reduced from 15px
          margin: 4px !important; // Reduced from 5px
          box-shadow: 0 2px 8px rgba(0,0,0,0.03);
        }
        
        /* Responsive styles for the board */
        @media (max-width: 768px) {
          .react-kanban-column {
            min-width: 300px !important;
            max-width: 300px !important;
            width: 300px !important;
          }
        }
        
        @media (max-width: 480px) {
          .react-kanban-column {
            min-width: 280px !important;
            max-width: 280px !important;
            width: 280px !important;
          }
        }
      `}</style>
      <ToastContainer position="top-right" autoClose={3000} />
      <Grid container spacing={0}>
        <Grid item xs={12}>
          {/* Custom Kanban Board with Pagination at Bottom */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: { xs: "2px", sm: "4px", md: "8px" }, // Reduced padding further
              boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
              display: 'flex',
              gap: { xs: 0.75, sm: 1.25 }, // Tighter column gap
              overflowX: 'auto',
              pb: 2,
              maxHeight: '82vh',
              overflowY: 'auto',
              width: "100%",
            }}
          >
            {controlledBoard.columns.map((column) => {
              const totalCards = column.cards.length;
              const currentLimit = visibleCardsLimit[column.id] || initialVisibleCards;
              // Filter out any invalid cards and slice to current limit
              const visibleCards = (column.cards || [])
                .filter(card => card && card.id)
                .slice(0, currentLimit);

              // Theme based column colors
              const getColumnColors = (title) => {
                if (title === "Application Declined") return { bg: "#FEE2E2", text: "#B91C1C", border: "#EF4444" };
                if (title === "Application Review") return { bg: "#E0E7FF", text: "#4338CA", border: "#6366F1" };
                if (title === "Hired") return { bg: "#DCFCE7", text: "#15803D", border: "#22C55E" };
                return { bg: "#F0F4FF", text: "#1E3A8A", border: "#3B82F6" }; // Theme Blue for dynamic steps
              };
              const colColors = getColumnColors(column.title);

              return (
                <Paper
                  key={column.id}
                  sx={{
                    minWidth: { xs: '200px', sm: '230px', md: '250px' }, // Reduced from 310px
                    maxWidth: { xs: '200px', sm: '230px', md: '250px' },
                    width: { xs: '200px', sm: '230px', md: '250px' },
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  {/* Column Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      padding: { xs: "6px", sm: "8px" },
                      backgroundColor: colColors.bg,
                      borderBottom: `2px solid ${colColors.border}`,
                      borderRadius: '8px 8px 0 0',
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ 
                        fontFamily: 'Poppins', 
                        fontWeight: 600, 
                        fontSize: { xs: '11px', sm: '12px', md: '13px' },
                        color: colColors.text,
                        maxWidth: { xs: '120px', sm: '160px', md: '200px' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {
                        column.title === "Application Declined"
                          ? "Candidature refusée"
                          : column.title === "Application Review"
                            ? "Candidature en analyse"
                            : column.title === "Hired"
                              ? "Candidature acceptée"
                              : column.title
                      }
                    </Typography>

                    <Box>
                      {column.title !== "Application Declined" &&
                        column.title !== "Hired" && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              addStep(column.title);
                            }}
                            title={t("add_new_step_title")}
                            sx={{
                              padding: { xs: '2px', sm: '4px' }, // Reduced from 4/6
                            }}
                          >
                            <AddIcon fontSize="small" sx={{ fontSize: { xs: '14px', sm: '16px' } }} />
                          </IconButton>
                        )}

                      {column.title !== "Application Declined" &&
                        column.title !== "Application Review" &&
                        column.title !== "Hired" && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteStepAlert({
                                open: true,
                                stepName: column.title,
                              });
                            }}
                            title={t("remove_this_step")}
                            sx={{
                              padding: { xs: '2px', sm: '4px' }, // Reduced from 4/6
                            }}
                          >
                            <CloseIcon fontSize="small" sx={{ fontSize: { xs: '14px', sm: '16px' } }} />
                          </IconButton>
                        )}
                    </Box>
                  </Box>

                  {/* Cards Container */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: { xs: '300px', sm: '350px', md: '400px' },
                      maxHeight: 'calc(80vh - 100px)',
                      overflowY: 'auto',
                      p: 0.5, // Reduced from 1
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25, // Tighter gap (approx 2px)
                      border: '2px dashed transparent',
                      borderRadius: 1,
                      transition: 'border-color 0.2s ease',
                      '&.drag-over': {
                        borderColor: 'primary.main',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('drag-over');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('drag-over');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');

                      try {
                        const cardData = JSON.parse(e.dataTransfer.getData('text/plain'));
                        const sourceColumnId = cardData.sourceColumnId;
                        const targetColumnId = column.id;

                        if (sourceColumnId !== targetColumnId && cardData.card) {
                          // Create proper source and destination objects that match react-kanban format
                          const source = {
                            fromColumnId: sourceColumnId,
                            fromPosition: cardData.fromPosition ?? 0 
                          };
                          const destination = {
                            toColumnId: targetColumnId,
                            toPosition: column.cards.length // Add to end of column
                          };

                          // Call the existing handleCardMove function
                          handleCardMove(cardData.card, source, destination);
                        }
                      } catch (error) {
                        console.error('Error handling card drop:', error);
                      }
                    }}
                    onScroll={(e) => handleScroll(e, column.id, totalCards)}
                  >
                    {totalCards === 0 ? (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '200px',
                        color: 'text.secondary'
                      }}>
                        <Typography variant="body2" sx={{ fontFamily: 'Poppins' }}>
                          {t("candidates.nocandidatesstep", "No candidates in this step")}
                        </Typography>
                      </Box>
                    ) : (
                      visibleCards.map((card, index) => {
                        // Safety check to ensure card is valid
                        if (!card || !card.id) {
                          console.warn('Invalid card found:', card);
                          return null;
                        }

                        return (
                          <Box
                            key={card.id}
                            sx={{
                              mb: 1,
                              cursor: 'grab',
                              '&:hover': {
                                cursor: 'grab'
                              },
                              '&:active': {
                                cursor: 'grabbing'
                              }
                            }}
                            draggable
                            onDragStart={(e) => {
                              // Ensure we have a valid card object before creating drag data
                              if (card && card.id) {
                                const dragData = {
                                  card: card,
                                  sourceColumnId: column.id,
                                  cardId: card.id,
                                  fromPosition: index
                                };
                                e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
                                e.dataTransfer.effectAllowed = 'move';
                              } else {
                                e.preventDefault(); // Prevent drag if card is invalid
                              }
                            }}
                          >
                            {renderCard(card, { dragging: false })}
                          </Box>
                        );
                      })
                    )}
                  </Box>

                </Paper>
              );
            })}
          </Box>
        </Grid>
      </Grid>
      <Popover
        open={Boolean(hoveredNote)}
        anchorEl={anchorEl}
        onClose={handleNoteLeave}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ pointerEvents: "none" }}
      >
        {hoveredNote && (
          <Paper elevation={3} sx={{ p: 2, maxWidth: 300 }}>
            <Typography>
              <img
                src={require("assets/img/note.png")}
                alt="Logo"
                style={{ width: "40px", marginRight: "16px" }}
              />
              {hoveredNote.note}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Created: {new Date(hoveredNote.date).toLocaleString()}
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              Done in the {hoveredNote.step} {<br></br>} By{" "}
              {hoveredNote.recruiter_name}
            </Typography>
          </Paper>
        )}
      </Popover>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            overflow: "auto",
            width: { xs: "95%", sm: "90%", md: "85%" },
            maxWidth: { xs: "100%", sm: "900px", md: "1000px" },
            height: "auto", // Auto height based on content
            maxHeight: { xs: "90vh", sm: "85vh", md: "85vh" },
            minHeight: "auto", // Remove fixed minimum height
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            transition: "transform 0.3s ease-in-out",
          },
        }}
      >
        <DialogTitle sx={{ pb: "2%", borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="h5" fontWeight="bold">
            {selectedCandidate &&
              selectedCandidate.status !== "Application-Imported" && selectedCandidate.status !== "Application Spontane" &&
              selectedCandidate.status !== "Scraping-Email"
              ? t("add_new_event")
              : t("board.contact_candidate")}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            mt: 2,
            p: 3,
            overflow: "auto",
            position: "relative",
            minHeight: "auto", // Remove fixed minimum height
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
            }}
          >
            {/* Event Creation Section */}
            {selectedCandidate &&
              selectedCandidate.status !== "Application-Imported" && selectedCandidate.status !== "Application Spontane" &&
              selectedCandidate.status !== "Scraping-Email" && (
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    mb: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                    {t("add_new_event")}
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      autoFocus
                      label={t("event_title")}
                      fullWidth
                      value={eventTitle}
                      onChange={(e) => setEventTitle(e.target.value)}
                      variant="outlined"
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label={t("event_date_and_time")}
                        value={eventDate}
                        onChange={(newValue) => setEventDate(newValue)}
                        disablePast
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </LocalizationProvider>
                  </Stack>
                </Box>
              )}

            {/* Email Interface Section */}
            <Box
              sx={{
                p: 3,
                backgroundColor: "#fff",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: "#191970", fontWeight: 600 }}>
                {t("board.send_email_to_candidate")}
              </Typography>

              {emailStatus.type && (
                <Alert
                  severity={emailStatus.type}
                  sx={{ mb: 2 }}
                  onClose={() => setEmailStatus({ type: null, message: "" })}
                >
                  {emailStatus.message}
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Template Selection */}
                <Box
                  sx={{ position: "relative", mb: 2 }}
                  data-template-dropdown
                >
                  <Typography
                    sx={{
                      color: "#000",
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    {t("board.select_email_template")}
                  </Typography>
                  <TextField
                    fullWidth
                    value={
                      selectedTemplateId
                        ? availableTemplates.find(
                          (t) => t.id === selectedTemplateId
                        )?.name +
                        (availableTemplates.find(
                          (t) => t.id === selectedTemplateId
                        )?.design
                          ? ` (${t("board.visual")})`
                          : "") || ""
                        : ""
                    }
                    onChange={(e) => {
                      // Handle typing to filter templates
                      const searchValue = e.target.value.toLowerCase();
                      setTemplateSearchValue(searchValue);
                      setShowTemplateDropdown(true);
                    }}
                    onFocus={() => setShowTemplateDropdown(true)}
                    placeholder={t("select_a_template")}
                    disabled={emailLoading || loadingTemplates}
                    error={!!emailErrors.template}
                    helperText={emailErrors.template}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: 50,
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#B0B0B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                    variant="outlined"
                    InputProps={{
                      endAdornment: selectedTemplateId && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedTemplateId("");
                              setShowTemplateDropdown(false);
                              setTemplateSearchValue("");
                            }}
                          >
                            <CloseIcon size={16} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {showTemplateDropdown && (
                    <Paper
                      elevation={3}
                      sx={{
                        position: "absolute",
                        width: "100%",
                        maxHeight: "200px",
                        overflow: "auto",
                        zIndex: 1000,
                        marginTop: "4px",
                      }}
                    >
                      <List dense>
                        {availableTemplates
                          .filter(
                            (template) =>
                              !templateSearchValue ||
                              template.name
                                .toLowerCase()
                                .includes(templateSearchValue)
                          )
                          .map((template) => (
                            <ListItem key={template.id} disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  setSelectedTemplateId(template.id);
                                  setShowTemplateDropdown(false);
                                  setTemplateSearchValue("");
                                  handleEmailTemplateChange({
                                    target: { value: template.id },
                                  });
                                }}
                              >
                                <ListItemText
                                  primary={`${template.name} ${template.design
                                    ? `(${t("board.visual")})`
                                    : ""
                                    }`}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        {availableTemplates.filter(
                          (template) =>
                            !templateSearchValue ||
                            template.name
                              .toLowerCase()
                              .includes(templateSearchValue)
                        ).length === 0 && (
                            <ListItem>
                              <ListItemText
                                primary={t("board.no_templates_found")}
                              />
                            </ListItem>
                          )}
                      </List>
                    </Paper>
                  )}
                </Box>

                {/* Recipient Email */}
                <TextField
                  label={t("board.recipient_email")}
                  name="recipient_email"
                  value={emailFormData.recipient_email}
                  onChange={handleEmailFormChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  error={!!emailErrors.recipient_email}
                  helperText={emailErrors.recipient_email}
                  disabled={emailLoading}
                />

                {/* Placeholder Fields */}
                {emailPlaceholders.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("board.template_variables")}
                    </Typography>
                    {emailPlaceholders.map((placeholder) => (
                      <TextField
                        key={placeholder}
                        label={
                          placeholder.charAt(0).toUpperCase() +
                          placeholder.slice(1).replace("_", " ")
                        }
                        value={
                          emailFormData.placeholder_values[placeholder] || ""
                        }
                        onChange={(e) =>
                          handleEmailPlaceholderChange(
                            placeholder,
                            e.target.value
                          )
                        }
                        margin="normal"
                        variant="outlined"
                        size="small"
                        disabled={emailLoading}
                        sx={{
                          minWidth: 150,
                          maxWidth: 300,
                          width: "100%",
                          mb: 2, // Add bottom margin for spacing between fields
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Email Preview */}
                {selectedEmailTemplate && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      {t("board.email_preview")}
                    </Typography>

                    {/* Subject Line */}
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1,
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        minHeight: "35px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color: "#333",
                          fontSize: "0.8rem",
                          wordBreak: "break-word",
                          width: "100%",
                        }}
                      >
                        <strong>{t("subject")}:</strong>{" "}
                        {renderedEmail.subject ||
                          t("board.enter_template_values_preview")}
                      </Typography>
                    </Box>

                    {/* Email Content */}
                    <Box
                      sx={{
                        height: "250px",
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        backgroundColor: "#fff",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {isVisualTemplate ? (
                        <iframe
                          key={`${selectedEmailTemplate.id}-${JSON.stringify(
                            emailFormData.placeholder_values
                          )}`}
                          srcDoc={
                            renderedEmail.body ||
                            `<p>${t("board.loading_preview")}</p>`
                          }
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            display: "block",
                            backgroundColor: "#fff",
                          }}
                          title="Email Preview"
                          onLoad={(e) => {
                            try {
                              const iframeDoc = e.target.contentDocument;
                              if (iframeDoc) {
                                const style = iframeDoc.createElement("style");
                                style.textContent = `
                                  body {
                                    margin: 0;
                                    padding: 16px;
                                    font-family: Arial, sans-serif;
                                    word-wrap: break-word;
                                    font-size: 0.875rem;
                                    line-height: 1.4;
                                  }
                                  * { max-width: 100% !important; }
                                  img { max-width: 100% !important; height: auto !important; }
                                `;
                                iframeDoc.head.appendChild(style);
                              }
                            } catch (error) {
                              console.log("Could not style iframe content:", error);
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            overflow: "auto",
                            p: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              lineHeight: 1.5,
                              color: "#333",
                              fontSize: "0.875rem",
                              fontFamily: "Arial, sans-serif",
                            }}
                          >
                            {renderedEmail.body ||
                              t("board.enter_template_values_preview")}
                          </Typography>
                        </Box>
                      )}

                      {/* Live Update Indicator */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(0, 23, 231, 0.1)",
                          color: "#0017E7",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.7rem",
                          fontWeight: 500,
                        }}
                      >
                        {t("board.live").toUpperCase()}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            pt: "1%",
            borderTop: "1px solid #e0e0e0",
            gap: "2%",
            px: "3%",
            pb: "1%",
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              minHeight: 44,
              px: 3,
              color: COLORS.signIn,
              borderColor: COLORS.signIn,
              bgcolor: "transparent",
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                bgcolor: COLORS.Button_OnHold,
                borderColor: COLORS.signIn,
                color: COLORS.signIn,
              },
              "&:focus": {
                outline: "none",
                boxShadow: "none",
              },
            }}
          >
            {t("cancel")} {/* Translated text */}
          </Button>
          <Button
            onClick={
              selectedCandidate &&
                selectedCandidate.status !== "Application-Imported" && selectedCandidate.status !== "Application Spontane" &&
                selectedCandidate.status !== "Scraping-Email"
                ? handleSubmit
                : contact
            }
            type="button"
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : null}
            sx={{
              minHeight: 44,
              px: 3,
              color: COLORS.white,
              bgcolor: COLORS.signIn,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              border: "none",
              "&:hover": {
                bgcolor: COLORS.Button_OnHold,
                color: COLORS.white,
              },
              "&:focus": {
                outline: "none",
                boxShadow: "none",
              },
              "&:disabled": {
                bgcolor: "#ccc",
                color: "#666",
              },
            }}
          >
            {creating
              ? t("board.creating")
              : selectedCandidate &&
                selectedCandidate.status !== "Application-Imported" && selectedCandidate.status !== "Application Spontane" &&
                selectedCandidate.status !== "Scraping-Email"
                ? selectedEmailTemplate
                  ? t("board.add_event_send_email").toUpperCase()
                  : t("add_event")
                : t("board.contact_candidate").toUpperCase()}
            {/* Translated text */}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addTemplateDialogOpen}
        onClose={() => setAddTemplateDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("add_new_template")}</DialogTitle>{" "}
        {/* Translated text */}
        <DialogContent>
          <TextField
            name="name"
            label={t("new_template_name")} // Translated text
            value={newTemplate.name}
            onChange={handleNewTemplateChange}
            fullWidth
            sx={{ marginTop: "10px" }}
          />
          <InputTextarea
            name="content"
            placeholder={t("new_template_content")} // Translated text
            value={newTemplate.content}
            onChange={handleNewTemplateChange}
            rows={5}
            style={{ width: "100%", marginTop: "10px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddTemplateDialogOpen(false)}
            sx={{
              alignSelf: "flex-start",
              color: COLORS.signIn,
              border: "1px solid",
              borderColor: COLORS.grey,
              bgcolor: COLORS.Button_OnHold,
              ":hover": {
                bgcolor: COLORS.white,
                borderColor: COLORS.signIn,
                color: COLORS.signIn,
              },
            }}
          >
            {t("cancel")} {/* Translated text */}
          </Button>
          <Button
            onClick={handleAddTemplate}
            sx={{
              height: "100%",
              color: COLORS.white,
              border: "1px solid",
              borderColor: COLORS.grey,
              bgcolor: COLORS.signIn,
              ":hover": {
                bgcolor: COLORS.white,
                borderColor: COLORS.signIn,
                color: COLORS.signIn,
              },
            }}
          >
            {t("add_new_template")} {/* Translated text */}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openTemplate}
        fullWidth
        maxWidth="lg"
        sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 600 } }}
      >
        <DialogTitle>
          <Typography>{t("edit_message_template")}</Typography>
        </DialogTitle>
        <DialogContent>
          {editingTemplateId !== null && (
            <Box sx={{ marginTop: "10px" }}>
              <TextField
                name="name"
                label={t("template_name")}
                value={newTemplate.name}
                onChange={handleNewTemplateChange}
                fullWidth
                sx={{ marginBottom: "10px" }}
              />
              <InputTextarea
                name="content"
                value={newTemplate.content}
                onChange={handleNewTemplateChange}
                rows={5}
                style={{ width: "100%", marginBottom: "10px" }}
              />
              <Button
                onClick={handleUpdateTemplate}
                sx={{
                  height: "100%",
                  color: COLORS.white,
                  border: "1px solid",
                  borderColor: COLORS.grey,
                  bgcolor: COLORS.signIn,
                  ":hover": {
                    bgcolor: COLORS.white,
                    borderColor: COLORS.signIn,
                    color: COLORS.signIn,
                  },
                }}
              >
                Update Template
              </Button>
              <Button
                sx={{
                  alignSelf: "flex-start",
                  color: COLORS.signIn,
                  border: "1px solid",
                  borderColor: COLORS.grey,
                  bgcolor: COLORS.Button_OnHold,
                  ":hover": {
                    bgcolor: COLORS.white,
                    borderColor: COLORS.signIn,
                    color: COLORS.signIn,
                  },
                }}
                onClick={() => {
                  setEditingTemplateId(null);
                  setOpenTemplate(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {/* Add New Step Dialog */}
      <Dialog
        open={newStepDialog.open}
        onClose={() => setNewStepDialog({ ...newStepDialog, open: false })}
      >
        <DialogTitle>{t("add_new_step")}</DialogTitle> {/* Translated text */}
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("step_name")}
            type="text"
            fullWidth
            value={newStepDialog.newStepName}
            onChange={(e) =>
              setNewStepDialog({
                ...newStepDialog,
                newStepName: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNewStepDialog({ ...newStepDialog, open: false })}
            sx={{
              alignSelf: "flex-start",
              color: COLORS.signIn,
              border: "1px solid",
              borderColor: COLORS.grey,
              bgcolor: COLORS.Button_OnHold,
              ":hover": {
                bgcolor: COLORS.white,
                borderColor: COLORS.signIn,
                color: COLORS.signIn,
              },
            }}
          >
            {t("cancel")} {/* Translated text */}
          </Button>
          <Button
            onClick={handleAddNewStep}
            sx={{
              height: "100%",
              color: COLORS.white,
              border: "1px solid",
              borderColor: COLORS.grey,
              bgcolor: COLORS.signIn,
              ":hover": {
                bgcolor: COLORS.white,
                borderColor: COLORS.signIn,
                color: COLORS.signIn,
              },
            }}
          >
            {t("add")} {/* Translated text */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* All Notes Dialog */}
      <Dialog
        open={allNotesOpen}
        onClose={handleCloseAllNotes}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: "16px", padding: 1 }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: "Poppins", 
          fontWeight: 700, 
          fontSize: "1.25rem",
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          {t("all_notes")}
          <IconButton onClick={handleCloseAllNotes} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: "none" }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#4a5568" }}>
              {t("add_a_note")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t("type_note_here")}
                value={newNotes[currentCardId] || ""}
                onChange={(e) => setNewNotes({ ...newNotes, [currentCardId]: e.target.value })}
                multiline
                maxRows={3}
              />
              <Button
                variant="contained"
                onClick={() => addNote(currentCardId)}
                sx={{ 
                  bgcolor: "#0017E7", 
                  borderRadius: "8px",
                  px: 3,
                  textTransform: "none",
                  ":hover": { bgcolor: "#0000aa" }
                }}
              >
                {t("add")}
              </Button>
            </Box>
          </Box>

          <List sx={{ pt: 0 }}>
            {Object.values(notes[currentCardId] || []).length > 0 ? (
              Object.values(notes[currentCardId] || []).map((note) => (
                <ListItem 
                  key={note.id}
                  sx={{ 
                    mb: 1.5, 
                    bgcolor: "#f8f9fa", 
                    borderRadius: "12px",
                    alignItems: "flex-start",
                    px: 2
                  }}
                >
                  <Box sx={{ mt: 1, mr: 2 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        bgcolor: "rgba(0, 23, 231, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#0017E7"
                      }}
                    >
                      <NoteAddIcon fontSize="small" />
                    </Box>
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#2d3748", mb: 0.5 }}>
                        {note.note}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography variant="caption" sx={{ color: "#718096", display: "flex", alignItems: "center", gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 12 }} /> {note.recruiter_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#a0aec0" }}>
                          {new Date(note.date).toLocaleString()} • {note.step}
                        </Typography>
                      </Box>
                    }
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => deleteNote(currentCardId, note.id)}
                    sx={{ color: "#e53e3e", mt: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 4, opacity: 0.5 }}>
                <NoteAddIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2">{t("no_notes_yet")}</Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseAllNotes} 
            variant="outlined" 
            sx={{ borderRadius: "8px", textTransform: "none", color: "#4a5568", borderColor: "#e2e8f0" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Template Dialog */}
      <Dialog
        open={addTemplateDialogOpen}
        onClose={() => setAddTemplateDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("add_new_template")}</DialogTitle>{" "}
        {/* Translated text */}
        <DialogContent>
          <TextField
            name="name"
            label={t("new_template_name")}
            value={newTemplate.name}
            onChange={handleNewTemplateChange}
            fullWidth
            sx={{ marginTop: "10px" }}
          />
          <InputTextarea
            name="content"
            placeholder={t("new_template_content")}
            value={newTemplate.content}
            onChange={handleNewTemplateChange}
            rows={5}
            style={{ width: "100%", marginTop: "10px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddTemplateDialogOpen(false)}
            sx={{
              alignSelf: "flex-start",
              color: COLORS.signIn,
              border: "1px solid",
              borderColor: COLORS.grey,
              bgcolor: COLORS.Button_OnHold,
              ":hover": {
                bgcolor: COLORS.white,
                borderColor: COLORS.signIn,
                color: COLORS.signIn,
              },
            }}
          >
            {t("cancel")} {/* Translated text */}
          </Button>
          <Button
            onClick={handleAddTemplate}
            sx={{
              height: "100%",
              color: COLORS.white,
              border: "1px solid",
              borderColor: COLORS.grey,
              bgcolor: COLORS.signIn,
              ":hover": {
                bgcolor: COLORS.white,
                borderColor: COLORS.signIn,
                color: COLORS.signIn,
              },
            }}
          >
            {t("add_template")} {/* Translated text */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog
        open={openTemplate}
        fullWidth
        maxWidth="lg"
        sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 600 } }}
      >
        <DialogTitle>
          <Typography>{t("edit_message_template")}</Typography>{" "}
          {/* Translated text */}
        </DialogTitle>
        <DialogContent>
          {editingTemplateId !== null && (
            <Box sx={{ marginTop: "10px" }}>
              <TextField
                name="name"
                label={t("template_name")}
                value={newTemplate.name}
                onChange={handleNewTemplateChange}
                fullWidth
                sx={{ marginBottom: "10px" }}
              />
              <InputTextarea
                name="content"
                value={newTemplate.content}
                onChange={handleNewTemplateChange}
                rows={5}
                style={{ width: "100%", marginBottom: "10px" }}
              />
              <Button
                onClick={handleUpdateTemplate}
                sx={{
                  height: "100%",
                  color: COLORS.white,
                  border: "1px solid",
                  borderColor: COLORS.grey,
                  bgcolor: COLORS.signIn,
                  ":hover": {
                    bgcolor: COLORS.white,
                    borderColor: COLORS.signIn,
                    color: COLORS.signIn,
                  },
                }}
              >
                {t("update_template")} {/* Translated text */}
              </Button>
              <Button
                sx={{
                  alignSelf: "flex-start",
                  color: COLORS.signIn,
                  border: "1px solid",
                  borderColor: COLORS.grey,
                  bgcolor: COLORS.Button_OnHold,
                  ":hover": {
                    bgcolor: COLORS.white,
                    borderColor: COLORS.signIn,
                    color: COLORS.signIn,
                  },
                }}
                onClick={() => {
                  setEditingTemplateId(null);
                  setOpenTemplate(false);
                }}
              >
                {t("cancel")} {/* Translated text */}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Old SendEmailModal removed - now using embedded email interface */}

      {/* Delete Step Alert */}
      <CustomAlert
        open={deleteStepAlert.open}
        onClose={handleDeleteStepCancel}
        onConfirm={handleDeleteStepConfirm}
        text="Si vous supprimez cette étape, elle sera supprimée de toutes vos offres"
        type="warning"
        title="Delete Step"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <SimpleAlert
        open={openalert}
        onClose={handleCloseAlert}
        text="Ce candidat n'est pas un utilisateur, vous ne pouvez pas le bloquer de l'application."
        type='warning'
      />
    </>
  );
}